/**
 * Build-time, per-stage feature-flag registry — the single source of
 * truth for what is released on each deployment stage.
 *
 * Design (spec §2): config-as-code compiled into the bundle, default-off.
 * Granularity is hierarchical **section + page**: a page is released iff
 * its section flag AND its page flag are both on. Anything not listed is
 * off — safe for incremental release.
 *
 * The stage is resolved from `import.meta.env.VITE_APP_STAGE`
 * (injected by `vite.config.ts`, default `"dev"` locally). Only `"prod"`
 * maps to the prod registry; any other value (including unset) falls
 * back to `"dev"` so a misresolved stage can never accidentally reveal
 * dev features in production.
 *
 * Dead-code elimination (defense-in-depth): `activeFlags()` selects the
 * registry with a constant-foldable
 * `import.meta.env.VITE_APP_STAGE === "prod" ? PROD_FLAGS : DEV_FLAGS`.
 * Vite replaces `import.meta.env.VITE_APP_STAGE` with a string literal at
 * build time, so in a prod build the ternary folds to `PROD_FLAGS` and the
 * `DEV_FLAGS` branch is tree-shaken — the dev registry literals (e.g.
 * `"search-creds"`) never reach the prod bundle. This makes the SOP claim
 * "dev features never reach the prod bundle" literally true, not just a
 * runtime guard. Verify: `grep -r "search-creds" dist/assets/*.js`
 * returns nothing in a prod build, but is present in a dev build.
 *
 * Migration path: this module is the only place the registry lives. A
 * future swap to runtime/remote flags keeps the same `FeatureFlags`
 * shape behind `useFeatureAccess`, so call sites never change.
 */

export type Stage = "dev" | "prod";

export interface FeatureFlags {
  /** section id (matches `leftMenuModel` section ids) → released */
  sections: Record<string, boolean>;
  /** page id (matches `leftMenuModel` item ids) → released */
  pages: Record<string, boolean>;
}

/**
 * `dev` releases the started features: Winning → Search Creds, Closing &
 * Post-Close → both Send Search Closing Email and Email Template Editor, and
 * the AgendaGPT external-link shortcut (Client Engagement → AgendaGPT).
 * External-link shortcuts have no in-app page to build — they're "done" the
 * moment the menu item is wired to the URL, so they release as soon as the
 * link exists.
 *
 * **2026-08-10 release — delivery phases 1 + 2 ship.** Every page id in
 * `PHASE_PAGE_IDS[1]` (`tool-babychops-v2`, `my-imports`) and
 * `PHASE_PAGE_IDS[2]` (`bct`, `ai-review`, `dash-searches`, `dash-detail`) is
 * now `true` on BOTH registries. (2026-09-02: prod temporarily re-diverged
 * for the production-freeze release — see the PROD_FLAGS docstring.) Consequences worth stating once, here:
 *
 *   - `?preview=1` / `?preview=2` become NO-OPS for those ids. The ladder
 *     reveals *not-yet-released* pages (`pageRevealedByMode` is only
 *     consulted when `released` is false), so there is nothing left for
 *     those rungs to reveal. Phases 3 and 4 were deleted on 2026-10-20 when
 *     their last pages (`company-lists`, `tool-chopbook`) were released;
 *     `RADAR_ONLY_PAGE_IDS` still need `?preview=all`.
 *   - A newly-released item can carry a dated "new" pill (`newBadge` in
 *     `leftMenuModel.ts`; the 2026-09-23 delivery's pills were removed on
 *     2026-10-20, leaving Combine & Filter and ChopBook; Company Lists got
 *     one on 2026-10-21) — the pill is the
 *     ONLY badge a released item can show (`showPreviewBadge` requires
 *     `!released` in `WebLeftMenu.tsx`), so without it the flip would land
 *     silently.
 *   - `ai-review` is LISTED for the first time. It was never in this map:
 *     `isPageReleased` reads `flags.pages[pageId] === true`, so an unlisted
 *     id is off — the phase ladder was the only thing that could ever reveal
 *     it. Releasing it therefore means ADDING the key, not flipping one.
 *   - `bct` is the third of three `bct`-shaped switches in this repo and the
 *     only two that live here (dev + prod). The third is
 *     `ENABLE_BCT` in `infra/api-stack.ts` — a BACKEND gate on whether
 *     `main.py` mounts the BCT routers at all. It was `false` on prod, so
 *     releasing this page without flipping that too would have shipped prod
 *     recruiters a form whose every call 404s. Both moved together.
 *   - `tool-babychops` is GONE from this map: Baby Chops v2 replaced the
 *     Baby Chops menu item outright (see `leftMenuModel.ts`), so the v1 page
 *     id no longer exists to gate. `/research/baby-chops` redirects to v2.
 *
 * Historical note on `my-imports`, since its flag has now moved three times:
 * ON in dev 2026-06-15 (imports stack integration), OFF everywhere
 * 2026-07-01 (operator directive — consistent hidden-by-default across
 * local/dev/prod while Imports matured), ON everywhere 2026-08-10 (this
 * release). The imports e2e specs still navigate with `?preview=1`; that is
 * now redundant but harmless, and left alone so this PR does not churn the
 * suite.
 */
export const DEV_FLAGS: FeatureFlags = {
  sections: {
    winning: true,
    linkedin: true,
    closing: true,
    "client-engagement": true,
    // Operations released on dev only so the migrated Searches Import page is
    // reachable; prod keeps the section off (its other items are still
    // coming-soon). Flip the matching PROD entries when Operations is ready.
    dashboards: true,
    // Outreach (Stage 3) — released on dev so the structural section + its
    // Sequences child page (a `ComingSoonPage` placeholder, no override yet)
    // are visible in the sidebar/grid. The underlying Sequences UI is not
    // built — the route falls back to ComingSoonPage via routes.tsx
    // `elementForItem`. Prod keeps this off (the page is still coming-soon
    // for real recruiters — see PROD_FLAGS).
    sequence: true,
  },
  pages: {
    "search-creds": true,
    // Phase 1 — RELEASED 2026-08-10. Was OFF 2026-07-01..2026-08-09.
    "my-imports": true,
    // Combine & Filter (Research) — RELEASED on BOTH stages (Jose,
    // 2026-10-07). It had no key until then (unlisted = off) and lived in
    // phase 3 beside `company-lists`; it left the phase alone, so phase 3 still
    // gates Company Lists on prod.
    "combine-filter": true,
    "closing-email": true,
    // `closing-email-template` removed from the flag list 2026-06-18 (Jared
    // feedback): the Closing Email Template Editor is no longer a top-level
    // sidebar destination — it lives as a secondary "Edit templates" button
    // inside Send Search Closing Email. Unlisted = off; the route stays
    // navigable via that in-page button (registered as a sub-resource of
    // `closing-email` in `SUB_RESOURCE_ROUTES` so it inherits the parent's
    // flag — `PAGE_OVERRIDES` entry was removed alongside the menu item).
    // Hidden 2026-08-13 — AgendaGPT survives as a BUTTON on Search Detail,
    // where the search id comes from the page instead of being typed. The form
    // page and its route are untouched and still work; only the menu entry is
    // gone. Flip back to true to revive it.
    "agenda-gpt": false,
    "tool-rocketreach": true,
    // NOTE: `tool-babychops` (Baby Chops v1) is intentionally absent as of
    // 2026-08-10 — Baby Chops v2 replaced the menu item, so there is no v1
    // page id left to gate. Unlisted = off, which is correct and moot:
    // `/research/baby-chops` is now a redirect to `/research/baby-chops-v2`
    // (declared in `routes.tsx`'s `LEGACY_REDIRECTS`), and a redirect route
    // is not flag-gated — the destination it lands on is.
    // Phase 1 — RELEASED 2026-08-10. This is now THE Baby Chops item (label
    // "Baby Chops", CWS-listing install flow), carrying an "Updated" pill
    // rather than a "new" one because the surface it replaces was already
    // live.
    "tool-babychops-v2": true,
    // Chopbook Tool install page — RELEASED on both stages 2026-10-20 (Jose),
    // with Company Lists: the "ChopBook" release. Pulled back behind
    // `?preview=4` from 2026-08-14 to QA the tool; Phase 4 and the
    // `?preview=chop-book` named preview were deleted with this flip.
    "tool-chopbook": true,
    "searches-import": true,
    // Admin console — behind ?preview=staff-members pending QA; protected by
    // AdminRoute (require_admin dep). Non-admins see nothing regardless of preview.
    // Do NOT add to PROD_FLAGS or flip to true until the console is fully QA'd.
    "administration": false,
    "staff-members": false,
    // Hidden 2026-06-18 (Jared feedback) — flip back to true to revive.
    "sync-phone": false,
    // `seq-info` (Outreach → Sequences) released so the section is non-empty
    // and visible to dev operators. The route renders the generic
    // `ComingSoonPage` since `seq-info` isn't in `routes.tsx` `PAGE_OVERRIDES`
    // — structural restoration only, the real Sequences UI ships later.
    "seq-info": true,
    // Phase 2 — RELEASED 2026-08-10 (was OFF from 2026-07-15). Two things had
    // to move with this one, and both are easy to miss:
    //   1. `routes.tsx`'s `BctRoute` gated the page CONTENT on
    //      `pageRevealedByMode(previewMode, "bct")` alone, so a released
    //      `bct` would still have rendered `<ComingSoonPage>` to a user with
    //      no `?preview`. It now checks released-OR-revealed.
    //   2. `infra/api-stack.ts`'s `ENABLE_BCT` was `false` on prod, so the
    //      prod API never mounted the BCT routers. Now true on every stage.
    bct: true,
    // Phase 2 — RELEASED 2026-08-10. Added to this map for the FIRST time:
    // `ai-review` was never listed, and `isPageReleased` treats an unlisted
    // id as off, so before today only the phase ladder could reveal it. The
    // real `<AiReviewPage />` has been wired in `PAGE_OVERRIDES` since the
    // AI Review POC landed, so this releases a built page, not a placeholder
    // (the `featureFlags.guardrail.test.ts` PROD interlock proves it).
    "ai-review": true,
    // Phase 2 — RELEASED 2026-08-10 (were OFF while the 2026-08-05 epic
    // landed). Releasing `dash-searches` changes what the Searches dashboard
    // card resolves to: `firstReleasedPageHref` walks leftMenuModel order and
    // My Searches now LEADS the section, so the card lands on
    // `/client-engagement/searches` instead of `/client-engagement/agenda`.
    // That is the designed end-state, not a regression — asserted in
    // `useFeatureAccess.test.tsx`.
    "dash-searches": true,
    // Hidden 2026-08-13 — the sidebar's parameter-less "Search Detail" entry
    // was a shortcut to the last search viewed, which is a worse door than the
    // list now that My Searches links every row's NAME straight to the detail.
    // The PAGE stays reachable: its route is registered under
    // `pageId: "dash-searches"` (routes.tsx), deliberately tied to the list's
    // flag, so hiding this entry orphans nothing. Flip back to true to revive
    // the menu item.
    "dash-detail": false,
    // Company Lists (epic 2026-09-25-chopbook-company-lists; replaces the old
    // "Company Targets" page) — ON on dev since 2026-10-01 (Jared's review),
    // RELEASED on prod 2026-10-20 with the ChopBook release (Jose).
    "company-lists": true,
    // Person side panel (epic 2026-09-21-person-side-panel, sub-task 05) —
    // ON on dev since 2026-09-22 and delivered to prod with the rest of
    // phase 2 on 2026-09-23, so `PHASE_PAGE_IDS[2]` is now a no-op for it on
    // both stages. Not a menu item, hence no pill: it gates the avatar button
    // in Import Detail and the standalone `/people/in/:handle` route (both
    // resolve it under the `linkedin` section).
    "person-panel": true,
    // Bullseye tab in Search Detail (epic 2026-10-05-bullseye) — RELEASED on
    // BOTH stages (Jose, 2026-10-07), visible with no preview. It was off on
    // both from 2026-10-06, reached on dev only through `?preview=5`, with
    // `PROD_PREVIEW_BLOCKED_PAGE_IDS` keeping every preview from revealing it
    // on prod; it left that block with this flip. Not a menu item: an in-page
    // tab of the `dash-searches` route (`IN_PAGE_FEATURE_IDS`), resolved under
    // `client-engagement`.
    bullseye: true,
    // Candidates tab on Search Detail (epic 2026-10-06-search-candidates-reports,
    // sub-task 02) — RELEASED on BOTH stages (Jose, 2026-10-07), visible with
    // no preview. It was preview-only (`?preview=6`) on every stage until this
    // flip. Not a menu item: it gates the `Candidates` tab inside the
    // `dash-searches` route (`?tab=candidates`), resolved under the
    // `client-engagement` section. The Dashboard's All Candidates panel — the
    // flag-off fallback — was deleted with the flip, so turning this back off
    // leaves Search Detail with no candidates table at all: revert the release
    // commit, not just this line.
    "search-candidates": true,
    // New Import from a Thrive search (epic 2026-10-15-thrive-search-import,
    // sub-task 03) — RELEASED on both stages 2026-10-20 (Jose). Thrive is now
    // the wizard's default source for everyone; "Upload a file" is the other
    // card (the E2E upload helpers click it first). The `?preview=thrive-import`
    // named preview was deleted with this flip. Not a menu item: it gates the
    // Source step inside `/research/imports/new` (`IN_PAGE_FEATURE_IDS`),
    // resolved under the `linkedin` section. The API side is
    // `ENABLE_THRIVE_SEARCH_IMPORT` in `infra/api-stack.ts`, on every stage.
    "thrive-search-import": true,
    // `four-reports` (Client Assessment + Backchannel References in the
    // Candidates tab, epic 2026-10-22-candidate-reports-four-kinds D5) was
    // released on both stages on 2026-10-26 and its entry DELETED in the same
    // PR (#1092): nothing read it once the web always offered the four kinds,
    // and a flag whose `false` hides nothing is a rollback trap. Rolling the
    // four kinds back means reverting PR #1092.
  },
};

/**
 * `prod` is the delivered surface. The **2026-09-02 production freeze ended
 * on 2026-09-23** (operator decision, Jose): everything the freeze held
 * back reaches recruiters today, announced with 30-day pills that were
 * removed on 2026-10-20 (Jose).
 *
 * Delivered on 2026-09-23 — `my-imports`, `bct`, `ai-review`,
 * `dash-searches`, `tool-babychops-v2` and `person-panel` flip to `true`,
 * and the prod-only transition surface is gone with them: the
 * `tool-babychops` entry (Baby Chops v1, the Drive-ZIP install page), the
 * `IS_PROD_STAGE` branches in `leftMenuModel.ts` (the v1 nav item and the
 * "Client Engagement" section naming) and the stage-conditional redirect in
 * `routes.tsx` — `/research/baby-chops` now redirects to v2 on every stage,
 * as it already did on dev.
 *
 * What still diverges, and why:
 *   - `agenda-gpt` ON here, hidden on dev since 2026-08-13: dev reaches
 *     AgendaGPT through the button on Search Detail. Prod keeps the sidebar
 *     entry until that path is confirmed with recruiters — removing a door
 *     they use today is a separate decision from this delivery.
 *   - `dash-detail`, `sync-phone`: off on BOTH registries.
 *
 * Released 2026-10-20 (Jose): `tool-chopbook` + `company-lists` (the
 * ChopBook release) and `thrive-search-import`, on both registries.
 * Released 2026-10-26: the four candidate report kinds (`four-reports`, whose
 * entry was deleted with the release — see the dev entry's note).
 *
 * With phases 1 and 2 released on both stages, `?preview=1` / `?preview=2`
 * are no-ops for those pages (`pageVisible` consults the ladder only for
 * pages that are not released). `PHASE_PAGE_IDS` keeps them as the RECORD
 * of what shipped when.
 */
export const PROD_FLAGS: FeatureFlags = {
  sections: {
    winning: true,
    linkedin: true,
    closing: true,
    "client-engagement": true,
    dashboards: true,
    sequence: true,
  },
  pages: {
    "search-creds": true,
    // Delivered 2026-09-23 (freeze lifted).
    "my-imports": true,
    // Combine & Filter — RELEASED, like dev (Jose, 2026-10-07).
    "combine-filter": true,
    "closing-email": true,
    // Still prod-only ON after the 2026-09-23 delivery: dev reaches
    // AgendaGPT through the Search Detail button, prod keeps the sidebar
    // entry until that is confirmed with recruiters (docstring above).
    "agenda-gpt": true,
    "tool-rocketreach": true,
    // `tool-babychops` (Baby Chops v1) was retired on 2026-09-23 with the
    // freeze: v2 IS the Baby Chops item on both stages now, and
    // `/research/baby-chops` redirects to it. Unlisted = off, as on dev.
    // Delivered 2026-09-23 — the Chrome Web Store install flow.
    "tool-babychops-v2": true,
    // Chopbook Tool install page — RELEASED 2026-10-20 (Jose), like dev.
    "tool-chopbook": true,
    "searches-import": true,
    // Hidden 2026-06-18 (Jared feedback) — flip back to true to revive.
    "sync-phone": false,
    "seq-info": true,
    // Delivered 2026-09-23. The backend `ENABLE_BCT` was already true on
    // prod (`infra/api-stack.ts`), so the UI now matches the API it calls.
    bct: true,
    // Delivered 2026-09-23 (freeze lifted).
    "ai-review": true,
    // Delivered 2026-09-23 (freeze lifted).
    "dash-searches": true,
    // Hidden 2026-08-13 on both stages — the parameter-less sidebar entry
    // was retired; the PAGE is registered under `dash-searches` (routes.tsx).
    "dash-detail": false,
    // Company Lists — RELEASED 2026-10-20 with ChopBook (Jose). The backend
    // `ENABLE_PITCHBOOK` was already true on prod (#1037).
    "company-lists": true,
    // Person side panel — delivered 2026-09-23 with the rest of phase 2
    // (Jose). Not a menu item, so it carries no pill: it is the avatar
    // button in Import Detail and the `/people/in/:handle` page.
    "person-panel": true,
    // Bullseye tab — RELEASED, like dev (Jose, 2026-10-07; see the dev entry).
    bullseye: true,
    // Candidates tab — RELEASED, like dev (Jose, 2026-10-07; see the dev entry).
    "search-candidates": true,
    // New Import from a Thrive search — RELEASED 2026-10-20, like dev (see
    // the dev entry).
    "thrive-search-import": true,
    // `four-reports` — released 2026-10-26 and deleted (see the dev entry).
  },
};

/**
 * Per-stage registry. Kept as a stable public export for tests/tooling
 * that look up a stage by name. NOTE: do NOT use this object to select the
 * active registry at runtime — indexing `FLAGS[resolveStage()]` keeps BOTH
 * branches reachable, so the dev literals would survive into the prod
 * bundle. `activeFlags()` uses a constant-foldable ternary instead so the
 * unused branch is dead-code-eliminated.
 */
export const FLAGS: Record<Stage, FeatureFlags> = {
  dev: DEV_FLAGS,
  prod: PROD_FLAGS,
};

/** Resolve the active stage from the build-time env. */
export function resolveStage(): Stage {
  return import.meta.env.VITE_APP_STAGE === "prod" ? "prod" : "dev";
}

/**
 * The active registry for the resolved stage.
 *
 * Selected via a constant-foldable ternary on the build-time-replaced
 * `import.meta.env.VITE_APP_STAGE` literal so the inactive registry is
 * tree-shaken from the bundle (see the module docstring). The public
 * contract is unchanged — callers still get the `FeatureFlags` for the
 * active stage.
 */
export function activeFlags(): FeatureFlags {
  return import.meta.env.VITE_APP_STAGE === "prod" ? PROD_FLAGS : DEV_FLAGS;
}

/** A section is released iff it is explicitly listed on (default-off). */
export function isSectionReleased(flags: FeatureFlags, sectionId: string): boolean {
  return flags.sections[sectionId] === true;
}

/**
 * A page is released iff BOTH its section flag and its page flag are on
 * (the hierarchy rule). Unlisted ids are off.
 */
export function isPageReleased(flags: FeatureFlags, sectionId: string, pageId: string): boolean {
  return isSectionReleased(flags, sectionId) && flags.pages[pageId] === true;
}
